import {
    StyledMessageContent,
    StyledSelectButtonLabel,
    StyledSelectButtonOptions,
    StyledSelectButtonWrapper,
    StyledSelectContainer,
    StyledSelectLinkWrapper,
    StyledSendButtonContainer,
    link,
    linkText,
} from './styles'
import {ArrowTopRightOnSquareIcon} from 'components/Icon'
import Button from 'components/Button'
import {NinchatMessages} from 'api/types'
import {NinchatUIComposeMessage} from 'api/types/messages'

import React from 'react'
import {UIMessageProps} from 'components/Messages/types'
import {parseMessages} from 'utils/uicompose.messages'
import {sendMessage} from 'api/action/sendMessage'
import {styled} from 'stitches/config'

import {useAppDispatch} from 'hooks'
import {useTranslation} from 'react-i18next'

const StyledLink = styled('a', link)
const StyledLinkText = styled('span', linkText)

const getColor = (classNames: string[]) => {
    if (classNames.includes('primary')) {
        return 'filled'
    } else if (classNames.includes('tertiary')) {
        return 'text'
    }
    return undefined
}

const UiButtonComponent: React.FC<{messages: Array<NinchatUIComposeMessage>, handlePress: (item: NinchatUIComposeMessage) => (event: any) => void}> = ({
    messages, handlePress,
}) => (
    <StyledSelectButtonWrapper>
        {messages.map((message) => {
            const classNames = message?.class?.split(' ') || []
            const isFullWidth = classNames.includes('full-width')
            const color = getColor(classNames)

            return (
                <Button
                    color={color ? color : undefined}
                    css={isFullWidth ? {width: '100%'} : undefined}
                    key={message.id}
                    onPress={handlePress(message)}
                    text={message.label}
                    type='button'
                />
            )
        })}
    </StyledSelectButtonWrapper>
)

const UiLinkComponent: React.FC<{messages: Array<NinchatUIComposeMessage>, handlePress: (item: NinchatUIComposeMessage) => (event: any) => void}> = ({
    messages, handlePress,
}) => (
    <StyledSelectLinkWrapper>
        {messages.map((message) => {
            const classNames = message?.class?.split(' ') || []
            const color = getColor(classNames)
            const isFullWidth = classNames.includes('full-width')

            return (
                <StyledLink
                    color={color}
                    css={isFullWidth ? {width: '100%'} : undefined}
                    href={message.href}
                    key={message.id}
                    onClick={handlePress(message)}
                    rel='noopener noreferrer'
                    target='_blank'
                >
                    <StyledLinkText color={color} multiline={true}>{message.label}</StyledLinkText>
                    <ArrowTopRightOnSquareIcon />
                </StyledLink>
            )
        })}
    </StyledSelectLinkWrapper>
)


const UiSelectComponent: React.FC<{
    messages: Array<NinchatUIComposeMessage>,
    handlePress: (item: NinchatUIComposeMessage) => (event?: React.MouseEvent<HTMLButtonElement>) => void,
}> = ({
    messages, handlePress,
}) => {
    const {t} = useTranslation()
    const [selectedValues, setSelectedValues] = React.useState<Record<string, string | undefined>>({})

    const handleSelect = (messageId: string | undefined, value: string) => () => {
        setSelectedValues((previous) => ({
            ...previous,
            [messageId ?? '']: value,
        }))
    }

    const handleSubmit = (message: NinchatUIComposeMessage) => () => {
        const selectedValue = selectedValues[message.id ?? '']
        handlePress({
            ...message,
            options: message.options?.map((option) => ({
                ...option,
                selected: option.value === selectedValue,
            })),
        })()
    }

    return (
        <StyledSelectContainer>
            {messages.map((message) => {
                const selectedValue = selectedValues[message.id ?? '']

                return (
                    <React.Fragment key={message.id}>
                        <StyledSelectButtonLabel>
                            {message.label}
                        </StyledSelectButtonLabel>
                        <StyledSelectButtonOptions>
                            {message?.options?.map((option) => (
                                <Button
                                    color={option.value === selectedValue ? 'filled' : undefined}
                                    css={{
                                        justifyContent: 'flex-start',
                                        width: '100%',
                                    }}
                                    key={option.value}
                                    onPress={handleSelect(message.id, option.value)}
                                    text={option.label}
                                    type='button'
                                />
                            ))}
                        </StyledSelectButtonOptions>
                        <StyledSendButtonContainer>
                            <Button
                                disabled={selectedValue === undefined}
                                onPress={handleSubmit(message)}
                                text={t('Send')}
                                type='button'
                            />
                        </StyledSendButtonContainer>
                    </React.Fragment>
                )
            })}
        </StyledSelectContainer>
    )
}

const ComposeMessage: React.FC<{channelId?: string, isLast?: boolean, message: UIMessageProps['message']}> = ({
    channelId, isLast, message,
}) => {
    const messages = parseMessages(message) ?? {}
    const dispatch = useAppDispatch()

    const sendUIAction = (item: NinchatUIComposeMessage) => () => {
        dispatch(sendMessage({
            ...channelId && {channel_id: channelId},
            message_type: NinchatMessages.NinchatMessageTypes.NinchatUIActionMessage,
            payload: [JSON.stringify({
                action: 'click',
                target: item,
            })],
        }))
    }

    const renderedGroups = Object.entries(messages).map(([currentKey, groupMessages]) => {
        const [{element}] = groupMessages

        // Links stay visible on every message, not only the last one
        if (element === 'a') {
            return <UiLinkComponent handlePress={sendUIAction} key={currentKey} messages={groupMessages} />
        }

        // Buttons and selects are interactive actions, only valid on the last message
        if (!isLast) {
            return null
        }

        if (element === 'button') {
            return <UiButtonComponent handlePress={sendUIAction} key={currentKey} messages={groupMessages} />
        } else if (element === 'select') {
            return <UiSelectComponent handlePress={sendUIAction} key={currentKey} messages={groupMessages} />
        }

        return null
    })
        .filter(Boolean)

    if (renderedGroups.length === 0) {
        return null
    }

    return (
        <StyledMessageContent>
            {renderedGroups}
        </StyledMessageContent>
    )
}

ComposeMessage.defaultProps = {
    channelId: undefined,
    isLast: undefined,
}

export default ComposeMessage
